/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { ProjectID, ProjectType } from '@modules/ui-types/projects'
import { useProjectCpsContext } from '@modules/project-components/ProjectCps/ProjectCpsContext'

type UnlinkProjectsParams = {
  projectId: ProjectID
  projectType: ProjectType
  projectIdsToUnlink: ProjectID[]
}

export const useUnlinkProjects = () => {
  const {
    usePatchElasticsearchProjectMutation,
    usePatchObservabilityProjectMutation,
    usePatchSecurityProjectMutation,
  } = useProjectCpsContext()

  const patchElasticsearchMutation = usePatchElasticsearchProjectMutation()
  const patchObservabilityMutation = usePatchObservabilityProjectMutation()
  const patchSecurityMutation = usePatchSecurityProjectMutation()

  const getPatchMutation = (projectType: ProjectType) => {
    switch (projectType) {
      case 'elasticsearch':
        return patchElasticsearchMutation
      case 'observability':
        return patchObservabilityMutation
      case 'security':
        return patchSecurityMutation
      default:
        return patchElasticsearchMutation
    }
  }

  const unlinkProjects = async ({
    projectId,
    projectType,
    projectIdsToUnlink,
  }: UnlinkProjectsParams) => {
    const mutation = getPatchMutation(projectType)

    // Build the linked projects structure with null values for projects to unlink
    const projectsToUpdate: Record<string, { type: ProjectType } | null> = {}

    projectIdsToUnlink.forEach((projectIdToUnlink) => {
      projectsToUpdate[projectIdToUnlink] = null
    })

    return mutation.mutateAsync({
      id: projectId,
      body: {
        linked: {
          projects: projectsToUpdate,
        },
      },
    })
  }

  return {
    unlinkProjects,
    isLoading:
      patchElasticsearchMutation.isLoading ||
      patchObservabilityMutation.isLoading ||
      patchSecurityMutation.isLoading,
    error:
      patchElasticsearchMutation.error ||
      patchObservabilityMutation.error ||
      patchSecurityMutation.error,
  }
}
